import { inject, Injectable } from '@angular/core';

import { TinyColor } from '@ctrl/tinycolor';
import { select, Store } from '@ngrx/store';

import { each, has, map } from 'lodash-es';

import { DefaultColorEnum } from '../../enums/default-color';
import * as fromChartActions from '../../store/actions/chart.actions';
import * as fromWhiteLabelingSelectors from '../../store/selectors/white-labeling.selectors';
import { ThemeColors } from '../../typings/theme';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private store = inject(Store);

  colors: ThemeColors = {
    danger: DefaultColorEnum.danger,
    dark: DefaultColorEnum.dark,
    light: DefaultColorEnum.light,
    medium: DefaultColorEnum.medium,
    primary: DefaultColorEnum.primary,
    secondary: DefaultColorEnum.secondary,
    success: DefaultColorEnum.success,
    tertiary: DefaultColorEnum.secondary,
    warning: DefaultColorEnum.warn
  };

  constructor() {
    this.store.pipe(select(fromWhiteLabelingSelectors.selectColors)).subscribe((colors) => this.setColors(colors));
  }

  setColors(colors: ThemeColors) {
    each(colors, (value: string, key: string) => {
      if (has(this.colors, key)) {
        const color = new TinyColor(value);
        if (color.isValid) {
          this.colors[key] = color.toHexString();
        }
      }
    });

    this.updateTheme();
    this.updateChartColorPalette(colors);
  }

  updateTheme() {
    each(this.colors, (value: string, key: string) => {
      this.updateThemeColor(key, value);
    });
  }

  updateThemeColor(name: string, value: string) {
    const color = new TinyColor(value);
    let contrast = new TinyColor('#000');

    this.setDOMColor(`--ion-color-${name}`, color.toHexString());
    this.setDOMColor(`--ion-color-${name}-rgb`, color.toRgbString());
    this.setDOMColor(`--ion-color-${name}-shade`, color.darken(10).toHexString());
    this.setDOMColor(`--ion-color-${name}-tint`, color.lighten(10).toHexString());

    if (color.isDark()) {
      contrast = new TinyColor('#FFF');
    }

    this.setDOMColor(`--ion-color-${name}-contrast`, contrast.toHexString());
    this.setDOMColor(`--ion-color-${name}-contrast-rgb`, contrast.toRgbString());
  }

  setDOMColor(name: string, value: string) {
    document.documentElement.style.setProperty(name, value);
  }

  updateChartColorPalette(colors: ThemeColors) {
    const primaryColor = new TinyColor(colors.primary);
    const alphaNumbers = [1, 0.2, 0.4, 0.6, 0.8];
    const chartColors = map(alphaNumbers, (alpha) => primaryColor.setAlpha(alpha).toRgbString());

    this.store.dispatch(
      fromChartActions.updateChartColors({
        payload: {
          colors: chartColors
        }
      })
    );
  }
}
